<template>
  <!--
    ContactForm — the Contact_Page enquiry form (Requirements 7.1, 7.4, 7.5, 9.6).

    Responsibilities kept inside this component:
      - Render name/email/phone/subject/message fields, each with a
        programmatically associated label (Vuetify renders <label for> bound to
        the field id we supply, satisfying Requirement 9.6).
      - Validate per field using the pure `validateEnquiry` helper — never a
        reimplementation (Requirements 7.4).
      - Manage submit / confirmation / error UI states and retain the visitor's
        entered values when a submission fails (Requirement 7.5).

    The actual network submission is delegated: the parent (ContactPage, task
    12.3) supplies `submitHandler`, a function returning a Promise. The form
    awaits it to drive the success/error state, and also emits a `submit` event
    carrying the validated enquiry for parents that prefer event wiring.
  -->
  <v-form
    novalidate
    class="contact-form"
    @submit.prevent="handleSubmit"
  >
    <h2 class="text-h5 mb-4">{{ form.heading }}</h2>

    <!--
      Honeypot — a field real visitors never see (positioned far off-screen and
      removed from the tab order), so only an automated form-filler populates it.
      A non-empty value is treated as a bot server-side. Kept as a plain input,
      not a Vuetify field, so it stays out of the visible form entirely.
    -->
    <div class="hp-field" aria-hidden="true">
      <label :for="honeypotName">Company website (leave blank)</label>
      <input
        :id="honeypotName"
        v-model="honeypot"
        type="text"
        :name="honeypotName"
        tabindex="-1"
        autocomplete="off"
      >
    </div>

    <!-- Confirmation state (Requirement 7.3, shown by this form on success). -->
    <v-alert
      v-if="status === 'success'"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ form.successText }}
    </v-alert>

    <!-- Error state: submission failed; entered values are retained below. -->
    <v-alert
      v-if="status === 'error'"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ form.errorText }}
    </v-alert>

    <v-text-field
      id="contact-name"
      v-model="fields.name"
      :label="form.fields.name.label"
      :placeholder="form.fields.name.placeholder"
      :error-messages="errors.name || []"
      name="name"
      autocomplete="name"
      variant="outlined"
      @blur="validateField('name')"
    />

    <v-text-field
      id="contact-email"
      v-model="fields.email"
      :label="form.fields.email.label"
      :placeholder="form.fields.email.placeholder"
      :error-messages="errors.email || []"
      name="email"
      type="email"
      autocomplete="email"
      variant="outlined"
      @blur="validateField('email')"
    />

    <v-text-field
      id="contact-phone"
      v-model="fields.phone"
      :label="form.fields.phone.label"
      :placeholder="form.fields.phone.placeholder"
      :error-messages="errors.phone || []"
      name="phone"
      type="tel"
      autocomplete="tel"
      variant="outlined"
      @blur="validateField('phone')"
    />

    <v-text-field
      id="contact-subject"
      v-model="fields.subject"
      :label="form.fields.subject.label"
      :placeholder="form.fields.subject.placeholder"
      :error-messages="errors.subject || []"
      name="subject"
      variant="outlined"
      @blur="validateField('subject')"
    />

    <v-textarea
      id="contact-message"
      v-model="fields.message"
      :label="form.fields.message.label"
      :placeholder="form.fields.message.placeholder"
      :error-messages="errors.message || []"
      name="message"
      variant="outlined"
      rows="5"
      auto-grow
      @blur="validateField('message')"
    />

    <!--
      Visible spam-protection status. The check is a self-hosted proof-of-work
      (no third party, no account, no personal data); the work runs in the
      browser and this surfaces its state so a visitor can see the form is
      protected. Shown only when a challenge provider is wired in.
    -->
    <div
      v-if="challengeProvider"
      class="verify-status"
      :class="`verify-status--${verifyStatus}`"
      role="status"
      aria-live="polite"
    >
      <template v-if="verifyStatus === 'verified'">
        <v-icon icon="mdi-shield-check" size="20" class="verify-status__icon" />
        <span>Verified — you’re human</span>
      </template>
      <template v-else-if="verifyStatus === 'error'">
        <v-icon icon="mdi-shield-alert-outline" size="20" class="verify-status__icon" />
        <span>Security check will finish when you submit.</span>
      </template>
      <template v-else>
        <v-progress-circular
          indeterminate
          size="18"
          width="2"
          class="verify-status__icon"
        />
        <span>Verifying you’re human…</span>
      </template>
      <span class="verify-status__note">Spam protection · no personal data collected</span>
    </div>

    <v-btn
      type="submit"
      color="primary"
      variant="flat"
      size="large"
      class="text-none"
      :loading="status === 'submitting'"
      :disabled="status === 'submitting'"
    >
      {{ form.submitLabel }}
    </v-btn>
  </v-form>
</template>

<script>
// ContactForm — fields, validation, and submit/confirmation/error states.
//
// Validation is delegated to the shared pure helper `validateEnquiry`; the
// network call is delegated to the `submitHandler` prop. This keeps the form
// focused and leaves API wiring to the ContactPage (task 12.3).
import { validateEnquiry } from 'services/validateEnquiry'
import { solveToPayload } from 'services/solveAltcha'
import contactContent from 'src/content/contact'

// The fields tracked by the form, in render order. Keys match the
// ContactEnquiry model consumed by `validateEnquiry`.
const FIELD_NAMES = ['name', 'email', 'phone', 'subject', 'message']

// Hidden field name the server's bot defence inspects (botDefense.HONEYPOT_FIELD).
const HONEYPOT_NAME = 'company_website'

function emptyFields () {
  return FIELD_NAMES.reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {})
}

export default {
  name: 'ContactForm',
  props: {
    // Optional async handler invoked with the validated enquiry. It should
    // return a Promise; the form awaits it to show the success or error state.
    // When absent, the form still validates and emits the `submit` event, and
    // treats a valid submission as successful.
    submitHandler: {
      type: Function,
      default: null,
    },
    // Optional async function returning a proof-of-work challenge (or null when
    // the server has it disabled). Supplied by the parent (ContactPage) so this
    // form stays transport-agnostic, exactly like `submitHandler`. When absent,
    // the form submits with no proof-of-work solution.
    challengeProvider: {
      type: Function,
      default: null,
    },
  },
  emits: ['submit'],
  data () {
    return {
      // Form copy and per-field labels live in structured content.
      form: contactContent.form,
      // Current field values; retained across a failed submission.
      fields: emptyFields(),
      // Per-field validation messages keyed by field name.
      errors: {},
      // UI state machine: idle | submitting | success | error.
      status: 'idle',
      // Honeypot value — must stay empty for a real submission.
      honeypot: '',
      honeypotName: HONEYPOT_NAME,
      // The current proof-of-work challenge, fetched on load and after each
      // attempt (challenges are single-use).
      challenge: null,
      // The solved proof-of-work payload, computed up front so it is ready at
      // submit time and the visible status can show "verified".
      altchaToken: null,
      // Visible verification state: idle (no check) | checking | verified | error.
      verifyStatus: 'idle',
    }
  },
  mounted () {
    // Fetch and solve a challenge up front so the visitor sees the protection
    // verify itself. The fill-time check measures from when the challenge was
    // issued (now), so solving early does not weaken it.
    this.prepareChallenge()
  },
  methods: {
    // Fetch a fresh proof-of-work challenge and solve it, driving the visible
    // status. Best-effort: on failure the submit path solves again as a fallback.
    async prepareChallenge () {
      if (!this.challengeProvider) {
        this.verifyStatus = 'idle'
        return
      }
      this.verifyStatus = 'checking'
      this.altchaToken = null
      try {
        this.challenge = await this.challengeProvider()
        if (!this.challenge) {
          // Server has proof-of-work disabled; nothing to show.
          this.verifyStatus = 'idle'
          return
        }
        this.altchaToken = await solveToPayload(this.challenge)
        this.verifyStatus = this.altchaToken ? 'verified' : 'error'
      } catch (err) {
        this.challenge = null
        this.verifyStatus = 'error'
      }
    },
    // Re-run validation and surface the message for a single field (used on
    // blur so visitors get feedback as they move through the form).
    validateField (field) {
      const { errors } = validateEnquiry(this.fields)
      if (errors[field]) {
        this.errors = { ...this.errors, [field]: errors[field] }
      } else {
        const next = { ...this.errors }
        delete next[field]
        this.errors = next
      }
    },
    // Validate the whole enquiry, then (if valid) emit and delegate submission.
    async handleSubmit () {
      const { valid, errors } = validateEnquiry(this.fields)
      this.errors = errors

      // Invalid input: show per-field messages and do not send (Requirement 7.4).
      if (!valid) {
        this.status = 'idle'
        return
      }

      const enquiry = { ...this.fields }
      this.emit('submit', enquiry)
      this.status = 'submitting'

      try {
        // Augment the enquiry with the bot-defence fields: the honeypot (kept
        // empty by real users) and the solved proof-of-work payload. Neither is
        // part of the enquiry the parent emits/validates.
        const payload = { ...enquiry, [HONEYPOT_NAME]: this.honeypot }

        if (this.challengeProvider) {
          // Normally the token was solved on load; if it is not ready yet (still
          // solving, or an earlier error), solve the current challenge now.
          if (!this.altchaToken && this.challenge) {
            this.altchaToken = await solveToPayload(this.challenge)
          }
          if (this.altchaToken) {
            payload.altcha = this.altchaToken
          }
        }

        if (this.submitHandler) {
          await this.submitHandler(payload)
        }
        // Success: confirm and clear the form for a fresh enquiry.
        this.status = 'success'
        this.fields = emptyFields()
        this.errors = {}
      } catch (err) {
        // Failure: show the error state and retain entered values (Requirement 7.5).
        this.status = 'error'
      } finally {
        // The challenge and its token are single-use (consumed by the server on
        // either outcome), so prepare a fresh one — which also re-runs the
        // visible verification — for any subsequent attempt.
        this.challenge = null
        this.altchaToken = null
        this.prepareChallenge()
      }
    },
    // Wrapper so $emit is easy to stub/spy in tests.
    emit (event, payload) {
      this.$emit(event, payload)
    },
  },
}
</script>

<style scoped>
.contact-form {
  width: 100%;
}

/* Honeypot: present in the DOM for bots, invisible and unreachable for humans
   (and screen readers, via aria-hidden + removal from the tab order). Using
   off-screen positioning rather than display:none so naive bots that skip
   hidden fields still fill it. */
.hp-field {
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

/* Visible spam-protection status row. */
.verify-status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  color: #4a4a4a;
}

.verify-status__icon {
  flex: none;
}

.verify-status--verified {
  color: #2e7d32;
}

.verify-status--error {
  color: #b26a00;
}

.verify-status__note {
  margin-left: auto;
  font-size: 0.75rem;
  color: #929292;
}
</style>
